import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { SvcComment } from '../../models/comment.model';
import { SvcCommentsService } from '../../services/svc-comments.service';
import { SvcCommentsEventsService } from '../../services/svc-comments-events.service';
import { catchError, finalize, map, of, tap } from 'rxjs';
import { HttpErrorService, SvcFunctionsHelper, SvcMediaQuery } from 'projects/lib-shared-common/src/public-api';
import { TranslocoService } from '@ngneat/transloco';
import { SvcToastService } from '@solvace/component/svc-toast';
import { FormBuilder, FormGroup } from '@angular/forms';
import { CommonPagination } from '@solvace/core/models';
import { UserService } from 'projects/lib-shared-core/src/public-api';
import { SvcCommentsMessageComponent } from '../svc-comments-message/svc-comments-message.component';
import { SvcCommentsInputComponent } from '../svc-comments-input/svc-comments-input.component';
import { SvcCommentMentionType } from '../../../svc-quill-editor-mention/svc-quill-editor-mention.directive';
import { ActionType } from '../../../svc-audio-to-text-button/svc-audio-to-text-button.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

enum AttachmentFileType {
  NORMAL,
  RECORDED_AUDIO,
}

@Component({
  selector: 'svc-comments-chat',
  templateUrl: './svc-comments-chat.component.html',
  styleUrls: ['./svc-comments-chat.component.scss'],
  providers: [SvcCommentsService],
})
export class SvcCommentsChatComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public referenceId: string | number;
  @Input() public referenceUid?: string | number;
  @Input() public referenceInternalId?: number;
  @Input() public configTypeId: string;
  @Input() public parentMessage: SvcComment;
  @Input('smallSize') public internalSmallSize: boolean = false;
  @Input() public applicationId: string;
  @Input() public placeholder = 'Adicionar comentário';
  @Input() public siteId: number;
  @Input() public external: boolean = false;
  @Input() public parentApplicationId: string;
  @Input() public parentRegisterId: number | string;
  @Input() public parentRegisterUId?: string;
  @Input() public parentOriginatorId?: string;
  @Input() public parentRegisterCode?: number;
  @Input() public parentRegistryCode?: number;
  @Input() public fromFeed = false;
  @Input() public postId?: string | number;
  /**
   * AB#60317 — optional cap on how many top-level comments this thread accepts. `null` (the
   * default) keeps the legacy unlimited behavior; a number hides the composer once `totalMessages`
   * reaches it. Used by the CIL execution answer controls, where a question whose
   * `mandatoryRules.commentOrImageWhen(Nok|Ok)` is on must be justified by exactly one Feed comment.
   */
  @Input() public maxComments: number | null = null;
  /**
   * AB#60317 — hides the composer’s send button so the host owns the send. Used by the CIL
   * execution answer, where the mandatory justification is posted by the answer’s Save button
   * (which must then wait for the CIL comment hub before persisting the answer).
   */
  @Input() public hideSendButton = false;

  public onCommentAdded = output<SvcComment>();
  public onCommentRemoved = output<SvcComment>();
  public commentsTotalChange = output<number>();

  @ViewChild('commentInput') public commentInput!: SvcCommentsInputComponent;

  @ViewChildren(SvcCommentsMessageComponent)
  public messagesCompList: QueryList<SvcCommentsMessageComponent>;

  public AttachmentFileType = AttachmentFileType;
  public isChild: boolean = false;
  public showInput: boolean = false;
  public totalMessages: number = 0;
  public messages: SvcComment[] = [];
  public chatForm: FormGroup;
  public attachmentFiles: { type: AttachmentFileType; file: Blob }[] = [];
  public isLoading: boolean = false;
  public isCreatingCommentLoading: boolean = false;
  public messagesAreGone: boolean = false;
  public commentIsValidToSend = false;
  public attachmentInput: HTMLInputElement;
  public suggestionsAdded: boolean;
  public actionType = ActionType;

  private pageSize: number;
  private pageIndex: number;

  public get messagesComp(): SvcCommentsMessageComponent[] {
    return this.messagesCompList?.toArray() || [];
  }

  /** AB#60317 — false once `maxComments` is reached, which hides the composer. */
  public get canSendComment(): boolean {
    return this.maxComments == null || this.totalMessages < this.maxComments;
  }

  public get smallSize(): boolean {
    return this.internalSmallSize || this._mediaQueryService.currentSize.isXS;
  }

  private _functionsHelper = inject(SvcFunctionsHelper);
  private _userService = inject(UserService);
  private _httpErrorService = inject(HttpErrorService);
  private _translocoService = inject(TranslocoService);
  private _svcToastService = inject(SvcToastService);
  private _commentsService = inject(SvcCommentsService);
  private _commentsEvents = inject(SvcCommentsEventsService);
  private _formBuilder = inject(FormBuilder);
  private _mediaQueryService = inject(SvcMediaQuery);
  private _destroyRef = inject(DestroyRef);
  private _cdr = inject(ChangeDetectorRef);
  private _refreshFromEvents = effect(() => {
    const commentEventInfo = this._commentsEvents.lastEvent();

    if (!commentEventInfo) return;

    const referenceUid = this.referenceUid ?? this.referenceId;
    const referenceInternalId = this.referenceInternalId;
    const postIdentifier = this.postId ?? this.referenceId;
    const matchesPost = commentEventInfo.postId !== undefined && `${commentEventInfo.postId}` === `${postIdentifier ?? ''}`;
    const matchesReference = `${commentEventInfo.referenceUid ?? commentEventInfo.referenceId ?? ''}` === `${referenceUid ?? ''}`;
    const matchesReferenceInternal = `${commentEventInfo.referenceInternalId ?? ''}` === `${referenceInternalId ?? ''}`;

    if ((!matchesPost && !matchesReference) || !matchesReferenceInternal) return;

    if (commentEventInfo.commentsTotal !== undefined) {
      this.totalMessages = commentEventInfo.commentsTotal;
      this.commentsTotalChange.emit(commentEventInfo.commentsTotal);
      this._cdr.markForCheck();
    }

    if (commentEventInfo.commentId) {
      if (this.messages.some((comment) => comment.commentId === commentEventInfo.commentId)) {
        return;
      }
      this.tryToUpdateFromCommentId(commentEventInfo.commentId);
    }
  });

  ngOnInit(): void {
    this.chatForm = this._formBuilder.group({
      message: [''],
    });
    this.chatForm
      .get('message')
      .valueChanges.pipe(
        takeUntilDestroyed(this._destroyRef),
        tap(() => this.validateSendButton())
      )
      .subscribe();
    this.isChild = !!this.parentMessage;
    this.showInput = !this.isChild;
    this.messagesAreGone = this.isChild && this.parentMessage.totalAnswers <= 0;
    this.pageSize = this.isChild ? 5 : 10;
    this.pageIndex = 1;
    this._getMessages();
    this._createInputFile();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const referenceIdChanged =
      'referenceId' in changes && changes.referenceId.previousValue !== changes.referenceId.currentValue;
    const referenceInternalIdChanged =
      'referenceInternalId' in changes && changes.referenceInternalId.previousValue !== changes.referenceInternalId.currentValue;
    const referenceUidChanged =
      'referenceUid' in changes && changes.referenceUid.previousValue !== changes.referenceUid.currentValue;
    const configTypeIdChanged =
      'configTypeId' in changes && changes.configTypeId.previousValue !== changes.configTypeId.currentValue;

    if ((referenceIdChanged || referenceInternalIdChanged || referenceUidChanged || configTypeIdChanged) && changes.referenceId?.previousValue != null) {
      this.refresh();
    }
  }

  private _createInputFile(): void {
    this.attachmentInput = document.createElement('input');
    this.attachmentInput.type = 'file';
    this.attachmentInput.onchange = (event: Event): void => {
      this.attachmentChanged(event);
    };
  }

  private _getMessages(): void {
    if (!this.messagesAreGone) {
      this.isLoading = true;
      const request = this.isChild
        ? this._commentsService.getParentComments(this.referenceId as number, {
            pageIndex: this.pageIndex,
            pageSize: this.pageSize,
            commentConfigTypeId: this.configTypeId,
          })
        : (typeof this.referenceId == 'number'
            ? this._commentsService.getCommentsId.bind(this._commentsService)
            : this._commentsService.getCommentsUid.bind(this._commentsService))(
            {
              pageIndex: this.pageIndex,
              pageSize: this.pageSize,
              referenceId: this.referenceId,
              ...(this.referenceUid != null && this.referenceUid !== ''
                ? { referenceUId: this.referenceUid }
                : typeof this.referenceId !== 'number'
                  ? { referenceUId: this.referenceId }
                  : {}),
              ...(this.referenceInternalId != null ? { referenceInternalId: this.referenceInternalId } : {}),
              commentConfigTypeId: this.configTypeId,
              externalSiteId: this.siteId,
            },
            this.external
          );
      request
        .pipe(
          tap((res: CommonPagination<SvcComment[]>) => {
            this.messages = this.pageIndex <= 1 ? res.data : [...this.messages, ...res.data];
            this.messagesAreGone = res.data.length < this.pageSize;
            this.totalMessages = res.total;
          }),
          catchError((error) => {
            const isRecordDeleted = error?.status === 404 || error?.status === 410;
            if (this.fromFeed && isRecordDeleted) {
              const message = this._translocoService.translate('Este chat não está mais disponível, pois o registro foi removido.');
              this._svcToastService.error(message);
            } else {
              this._httpErrorService.showErrorInToast(error);
            }
            return of({ data: [] as SvcComment[], total: 0 });
          }),
          finalize(() => {
            this.isLoading = false;
            // Default-CD component may live under OnPush ancestors (e.g. CIL execution modal);
            // mark for check so the loaded messages render and the spinner is removed.
            this._cdr.markForCheck();
          })
        )
        .subscribe();
    }
  }

  public refresh(): void {
    this.messagesAreGone = false;
    this.pageIndex = 1;
    this.messages = [];
    this._getMessages();
  }

  public tryToUpdateFromCommentId(commentId: number): void {
    this._commentsService
      .getCommentById(commentId)
      .pipe(
        tap((comment) => {
          if (comment.commentParentId > 0) {
            const messages = this.messagesComp;
            const index = messages.findIndex((m) => m.message.commentId === comment.commentParentId);
            if (index >= 0) {
              if (!messages[index].showChildrenMessages) {
                messages[index].message.totalAnswers += 1;
                messages[index].showMessages();
              } else {
                messages[index].svcCommentsChatComponent?.addNewCommentInList(comment);
              }
            }
          } else {
            this.addNewCommentInList(comment);
          }
          this._cdr.markForCheck();
        })
      )
      .subscribe();
  }

  public addNewCommentInList(message: SvcComment): void {
    const messageIndex = this.messages.findIndex((m) => m.commentId === message.commentId);
    if (messageIndex >= 0) {
      this.messages[messageIndex] = message;
    } else {
      this.messages = [message, ...this.messages];
    }
    this.onCommentAdded.emit(message);
    this._cdr.markForCheck();
  }

  public openAttachmentWindow(): void {
    if (!this.isCreatingCommentLoading) {
      this.attachmentInput.click();
    }
  }

  public attachmentChanged(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    for (let i = 0; i < files.length; i++) {
      this.addFileToAttachments(files[i], AttachmentFileType.NORMAL);
    }
    input.value = '';
  }

  public addFileToAttachments(file: Blob, type: AttachmentFileType): void {
    this.attachmentFiles.push({
      type: type,
      file: file instanceof File ? file : new File([file], 'audio_recorded.mp3'),
    });
    this.validateSendButton();
    // AB#69790 — the file arrives from the detached <input type="file"> change handler (and
    // AB#69789: from the recorder's stop event), never from a template event binding, so no
    // view is marked dirty. Under an OnPush host (e.g. CIL Comments modal) the attachment chip
    // stayed invisible until the next click anywhere in the chat re-ran change detection.
    this._cdr.markForCheck();
  }

  public sendMessage(): void {
    if (this.chatForm.invalid) {
      return;
    }

    const message = this.chatForm.value.message as string;
    const mentionUsers = this.commentInput.mentions.filter((x) => x.type === 1).map((x) => x.id as string);
    const mentionTeams = this.commentInput.mentions.filter((x) => x.type === 2).map((x) => x.id as number);
    const mentionHashTags = this.commentInput.mentions.filter((x) => x.type === 3).map((x) => x.id as number);
    const attachmentsFiles = this.attachmentFiles.map((x) => x.file);
    const parentRegisterId = typeof this.parentRegisterId === 'number' ? this.parentRegisterId : undefined;
    const parentRegisterUId = this.parentRegisterUId ?? (typeof this.parentRegisterId === 'string' ? this.parentRegisterId : undefined);

    this.commentInput?.setDisabled(true);
    this.isCreatingCommentLoading = true;
    (this.isChild
      ? this._commentsService.createAnswer(this.referenceId as number, {
          siteId: this.siteId ?? this._userService.user.lastSiteId,
          configTypeId: this.configTypeId,
          commentText: message,
          mentionUsers: mentionUsers,
          mentionTeams: mentionTeams,
          mentionHashTags: mentionHashTags,
          attachments: attachmentsFiles,
          parentApplicationId: this.parentApplicationId,
          parentRegisterId,
          parentRegisterUId,
          parentOriginatorId: this.parentOriginatorId,
          parentRegisterCode: this.parentRegisterCode,
          parentRegistryCode: this.parentRegistryCode,
        })
      : this._commentsService.createComment({
          referenceId: this.referenceId,
          referenceUid: this.referenceUid,
          referenceInternalId: this.referenceInternalId,
          siteId: this.siteId ?? this._userService.user.lastSiteId,
          configTypeId: this.configTypeId,
          commentText: message,
          mentionUsers: mentionUsers,
          mentionTeams: mentionTeams,
          mentionHashTags: mentionHashTags,
          attachments: attachmentsFiles,
          parentApplicationId: this.parentApplicationId,
          parentRegisterId,
          parentRegisterUId,
          parentOriginatorId: this.parentOriginatorId,
          parentRegisterCode: this.parentRegisterCode,
          parentRegistryCode: this.parentRegistryCode,
        })
    )
      .pipe(
        tap((message) => {
          this.chatForm.reset();
          this.attachmentFiles = [];
          this.messages = [message, ...this.messages];
          this.onCommentAdded.emit(message);
          if (this.parentMessage) this.showInput = false;

          this._emitCommentSent(message.commentId);

          this._fetchCommentsTotal().subscribe({
            next: (total) => {
              this.totalMessages = total;
              this.commentsTotalChange.emit(total);
              this._emitCommentSent(message.commentId, total);
              this._cdr.markForCheck();
            },
            error: () => {
              this._emitCommentSent(message.commentId, this.totalMessages);
            },
          });
        }),
        catchError((error) => {
          this._httpErrorService.showErrorInToast(error);
          return error;
        }),
        finalize(() => {
          this.commentInput?.setDisabled(false);
          this.isCreatingCommentLoading = false;
          this.suggestionsAdded = false;
          this._cdr.markForCheck();
        })
      )
      .subscribe();
  }

  public removeAttachment(file: File): void {
    const fileIndex = this.attachmentFiles.findIndex((item) => item.file === file);
    this.attachmentFiles.splice(fileIndex, 1);
    this.attachmentFiles = [...this.attachmentFiles];

    this.validateSendButton();
  }

  public commentRemoved(message: SvcComment): void {
    this.onCommentRemoved.emit(message);
    this.messages = this.messages.filter((x) => x.commentId !== message.commentId);
    this.totalMessages = Math.max(0, this.totalMessages - 1);
    this.commentsTotalChange.emit(this.totalMessages);
    this._cdr.markForCheck();
  }

  public nextPage(): void {
    this.pageIndex += 1;
    this._getMessages();
  }

  public focus(): void {
    if (this.showInput) {
      this.commentInput?.focus();
    } else {
      this.showInput = true;
      setTimeout(() => this.commentInput?.focus());
    }
  }

  public validateSendButton(): void {
    this.commentIsValidToSend = this._functionsHelper.removeHtml(this.chatForm.value.message ?? '') != '' || this.attachmentFiles.length > 0;
  }

  public loadSuggestions(): void {
    if (this.suggestionsAdded) return;
    this._commentsService
      .getCommentMentionSuggestion({
        commentId: this.referenceId,
      })
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap((res) => {
          const suggestions = [
            ...(res?.suggestionUsers?.map((u) => ({
              type: SvcCommentMentionType.USER,
              id: u.userId,
              name: u.userName,
            })) ?? []),
            ...(res?.suggestionTeams?.map((t) => ({
              type: SvcCommentMentionType.TEAM,
              id: t.teamId,
              name: t.teamName,
            })) ?? []),
          ];

          suggestions.forEach((s) => this.commentInput?.addMention(s.type, s.id, s.name));
          this.suggestionsAdded = true;
        })
      )
      .subscribe();
  }

  public ngOnDestroy(): void {
    this.attachmentInput?.remove();
    (this.attachmentInput as any)?.removeAllListeners?.();
  }

  private _fetchCommentsTotal() {
    const paramsBase: any = {
      commentConfigTypeId: this.configTypeId,
      pageIndex: 1,
      pageSize: 1,
      sortDirection: 2,
    };

    if (this.external && this.siteId) {
      paramsBase.externalSiteId = this.siteId;
    }

    if (typeof this.referenceId === 'number') {
      return this._commentsService
        .getCommentsId(
          {
            ...paramsBase,
            referenceId: this.referenceId as number,
            ...(this.referenceUid != null && this.referenceUid !== '' ? { referenceUId: this.referenceUid } : {}),
            ...(this.referenceInternalId != null ? { referenceInternalId: this.referenceInternalId } : {}),
          },
          this.external
        )
        .pipe(map((res) => res.total));
    }

    const uid = (this.referenceUid ?? this.referenceId) as string;
    return this._commentsService
      .getCommentsUid(
        {
          ...paramsBase,
          referenceId: this.referenceId,
          referenceUId: uid,
        },
        this.external
      )
      .pipe(map((res) => res.total));
  }

  private _emitCommentSent(commentId: number, commentsTotal?: number): void {
    this._commentsEvents.emitCommentSent({
      referenceId: this.referenceId,
      referenceInternalId: this.referenceInternalId,
      referenceUid: this.referenceUid ?? this.referenceId,
      configTypeId: this.configTypeId,
      applicationId: this.applicationId,
      siteId: this.external ? this.siteId : undefined,
      external: this.external,
      postId: this.postId ?? this.referenceId,
      commentId: commentId,
      commentsTotal: commentsTotal,
    });
  }
}
